import { Dialog as HDialog, Transition } from "@headlessui/react";
import { Fragment, type ReactNode } from "react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

export const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <HDialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HDialog.Panel
                className={`w-full ${sizes[size]} transform overflow-hidden rounded-xl
                  bg-white p-6 text-left align-middle shadow-xl
                  dark:bg-zinc-900 dark:border dark:border-zinc-800
                  transition-all`}
              >
                {title && (
                  <HDialog.Title
                    as="h2"
                    className="text-base font-semibold text-zinc-900 dark:text-zinc-100"
                  >
                    {title}
                  </HDialog.Title>
                )}
                {description && (
                  <HDialog.Description className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {description}
                  </HDialog.Description>
                )}
                {children && <div className="mt-4">{children}</div>}
                {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
              </HDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HDialog>
    </Transition>
  );
};
